
import { supabase } from "@/integrations/supabase/client";
import { MembershipPaymentResult } from "../types/membership";

// Confirmer une adhésion après paiement (via RPC pour bypass RLS)
export async function confirmMembershipPayment(membershipId: string, paymentId: string) {
  try {
    console.log(`Confirmation du paiement (RPC) pour session id=${paymentId}`);

    // 1) Récupérer l'adhésion via l'ID de session Stripe
    const { data: membershipData, error: fetchError } = await supabase
      .rpc('get_membership_by_session', { session_id: paymentId })
      .maybeSingle();

    if (fetchError) {
      console.error('Erreur RPC get_membership_by_session:', fetchError);
      throw new Error(`Erreur lors de la recherche de l'adhésion: ${fetchError.message}`);
    }

    if (!membershipData) {
      console.warn('Aucune adhésion trouvée pour cette session Stripe');
      throw new Error('Adhésion non trouvée ou déjà payée');
    }

    // 2) Marquer comme payée côté serveur (SECURITY DEFINER)
    const { data: markData, error: markError } = await supabase
      .rpc('mark_membership_paid', { session_id: paymentId });

    if (markError) {
      console.error('Erreur RPC mark_membership_paid:', markError);
      // Ne pas bloquer si déjà payée
    } else {
      console.log('Statut mis à jour via RPC:', markData);
    }

    // 3) Générer documents & envoyer les emails (edge functions)
    await processDocumentsAndSendEmail(membershipData);

    return {
      success: true,
      membershipId: membershipData.id,
      membershipData,
    };
  } catch (error) {
    console.error('Erreur lors de la confirmation du paiement:', error);
    throw error;
  }
}

// Fonction pour générer les documents et envoyer l'email
async function processDocumentsAndSendEmail(membershipData: any) {
  try {
    // Récupérer l'ID de signature de la charte pour cet utilisateur
    const { data: signatureData } = await supabase
      .from('charter_signatories')
      .select('id')
      .eq('email', membershipData.email)
      .eq('email_verified', true)
      .maybeSingle();
    
    const signatureId = signatureData?.id;
    console.log("ID de signature trouvé:", signatureId || "Non trouvé");
    
    // Générer le CERFA via la fonction dédiée
    try {
      console.log("Génération du CERFA pour l'adhésion:", membershipData.id);
      
      const cerfaResult = await supabase.functions.invoke("generate-cerfa", {
        body: {
          membershipId: membershipData.id,
          testData: {
            firstName: membershipData.first_name,
            lastName: membershipData.last_name,
            address: membershipData.address,
            postalCode: membershipData.postal_code,
            city: membershipData.city,
            amount: membershipData.amount,
            createdAt: new Date().toISOString()
          }
        }
      });
      
      if (cerfaResult.error) {
        console.error("Erreur lors de la génération du CERFA:", cerfaResult.error);
        throw cerfaResult.error;
      }
      
      console.log("CERFA généré avec succès:", cerfaResult.data);
      
      // Récupérer l'URL du CERFA généré
      const cerfaUrl = cerfaResult.data.cerfa_url;
      
      // Mettre à jour l'URL du CERFA dans la base
      if (cerfaUrl) {
        const { error: cerfaUpdateError } = await supabase
          .from("memberships")
          .update({ cerfa_url: cerfaUrl })
          .eq("id", membershipData.id);
        
        if (cerfaUpdateError) {
          console.error("Erreur lors de la mise à jour de l'URL du CERFA:", cerfaUpdateError);
        }
      }
      
      // Vérifier si c'est un don, auquel cas on n'inclut pas la charte
      const isDonor = membershipData.membership_type === "donor";
      const includeCharter = !isDonor; // Ne pas inclure la charte si c'est un donateur
      
      console.log(`Type d'adhésion: ${membershipData.membership_type}, Inclusion charte: ${includeCharter ? 'Oui' : 'Non'}`);
      
      // Envoi de l'email de confirmation avec CERFA et charte personnalisée uniquement si ce n'est pas un don
      console.log("Appel à la fonction send-cerfa-email avec les données:", {
        firstName: membershipData.first_name,
        lastName: membershipData.last_name,
        email: membershipData.email,
        membershipId: membershipData.id,
        cerfaUrl: cerfaUrl || null,
        signatureId: signatureId || undefined,
        includeCharter: includeCharter, // Ne pas inclure la charte pour les dons
        membershipType: membershipData.membership_type
      });
      
      const emailResult = await supabase.functions.invoke("send-cerfa-email", {
        body: {
          firstName: membershipData.first_name,
          lastName: membershipData.last_name,
          email: membershipData.email,
          membershipId: membershipData.id,
          cerfaUrl: cerfaUrl || null,
          signatureId: signatureId || undefined,
          includeCharter: includeCharter, // Ne pas inclure la charte pour les dons
          membershipType: membershipData.membership_type
        }
      });
      
      if (emailResult.error) {
        console.error("Erreur lors de l'envoi de l'email de confirmation:", emailResult.error);
        
        // Essayer une deuxième fois avec la fonction alternative
        console.log("Tentative avec send-membership-cerfa comme alternative...");
        const altEmailResult = await supabase.functions.invoke("send-membership-cerfa", {
          body: {
            firstName: membershipData.first_name,
            lastName: membershipData.last_name,
            email: membershipData.email,
            address: membershipData.address,
            city: membershipData.city,
            postalCode: membershipData.postal_code,
            membershipType: membershipData.membership_type,
            amount: membershipData.amount,
            includeCharter: includeCharter // Ne pas inclure la charte pour les dons
          }
        });
        
        if (altEmailResult.error) {
          console.error("Erreur lors de la tentative alternative d'envoi d'email:", altEmailResult.error);
          throw new Error(`Échec des deux tentatives d'envoi d'email: ${emailResult.error.message} et ${altEmailResult.error.message}`);
        } else {
          console.log("Email envoyé avec succès via la méthode alternative:", altEmailResult.data);
        }
      } else {
        console.log("Email de confirmation envoyé avec succès:", emailResult.data);
      }
    } catch (cerfaError) {
      console.error("Exception lors de la génération ou de l'envoi du CERFA:", cerfaError);
      // Tentative alternative en cas d'échec de la première méthode
      try {
        // Vérifier si c'est un don, auquel cas on n'inclut pas la charte
        const isDonor = membershipData.membership_type === "donor";
        const includeCharter = !isDonor; // Ne pas inclure la charte si c'est un donateur
        
        console.log("Tentative directe avec send-membership-cerfa suite à une erreur...");
        const directEmailResult = await supabase.functions.invoke("send-membership-cerfa", {
          body: {
            firstName: membershipData.first_name,
            lastName: membershipData.last_name,
            email: membershipData.email,
            address: membershipData.address,
            city: membershipData.city,
            postalCode: membershipData.postal_code,
            membershipType: membershipData.membership_type,
            amount: membershipData.amount,
            includeCharter: includeCharter // Ne pas inclure la charte pour les dons
          }
        });
        
        if (directEmailResult.error) {
          console.error("Échec de la tentative directe:", directEmailResult.error);
        } else {
          console.log("Email envoyé avec succès via la méthode directe:", directEmailResult.data);
        }
      } catch (directError) {
        console.error("Exception lors de la tentative directe:", directError);
      }
    }
  } catch (processingError) {
    console.error("Erreur lors du traitement des documents et de l'envoi d'email:", processingError);
    throw processingError;
  }
}
